import { frameId } from "../assets/manifest.ts";
import { frameSizeOf, initialFootprint } from "../compile/behaviour.ts";
import type { CompileContext } from "../compile/context.ts";
import type { PropFootprint } from "../compile/footprint.ts";
import type { Command } from "../document/commands.ts";
import { COLLISION_CELLS_PER_TILE, type MapDocument, type PropBehaviour, type PropInstance, type ZoneLayer } from "../document/types.ts";
import { activationLook } from "./activation-map.ts";
import type { ZoneTypeDefinition } from "./adapter.ts";
import type { ObjectTypeDefinition, ZoneStampDefinition } from "./objects.ts";
import { addLayerCommand, newLayer, requireProp, setPropBehaviourCommand } from "./panel-commands.ts";
import { paintZoneCommand } from "./zone-painting.ts";

/**
 * A type's zone stamp on a placed prop (ADR-096 decision 38): the cell a prop stands on, the stamp's mask laid
 * around it in the frame's own space, and the one command that paints the mask onto a zone layer and, when the stamp
 * says so, writes the same cells as the prop's activation. The zone is painted where the prop's activation cells
 * lie on the map (`activationLook`), so the two cover the same collision cells however the prop is placed or
 * mirrored. What the zone type means is the game's; nothing here reads it.
 */

/**
 * A cell in a frame's own space: its column from the frame's left edge and its rows up from the cell row above the
 * frame's bottom edge, as activation cells count (`[column, rowsUp]`).
 */
export interface FrameGroundCell {
  readonly column: number;
  readonly rowsUp: number;
}

/**
 * What a prop's type offers to paint: the zone type as the game registers it, and whether the command also writes
 * the prop's activation cells.
 */
export interface ZoneStampOffer {
  readonly zoneType: ZoneTypeDefinition;
  readonly activation: boolean;
}

/**
 * Why a press painted nothing, as a code the panel words: the prop's type declares no stamp (`no-stamp`), no pack
 * sizes its frame (`no-size`), its frame cannot be laid on the map (`off-map`), a layer the command would change is
 * locked or hidden (`locked`), or the store's session took no command, which the session tells its host itself
 * (`refused`).
 */
export type ZoneStampRefusal = "no-stamp" | "no-size" | "off-map" | "locked" | "refused";

/**
 * The cell a footprint stands on, in its frame's own space: the cell holding the point under the middle of the
 * lowest row it blocks, the middle taken across every run on that row. A footprint that blocks nothing stands on the
 * cell under the middle of the frame's bottom edge.
 *
 * @param footprint The frame's footprint: its size and, when it blocks, its runs.
 * @param cellPixels Pixels per collision cell.
 * @returns The cell, `rowsUp` -1 for the row under the frame.
 * @example footprintGroundCell({ w: 16, h: 32, base: 24, runs: [[0, 4, 11]] }, 8) is { column: 1, rowsUp: 0 }.
 */
export function footprintGroundCell(footprint: PropFootprint, cellPixels: number): FrameGroundCell {
  if (footprint.runs.length === 0) {
    return { column: Math.floor(footprint.w / 2 / cellPixels), rowsUp: -1 };
  }
  let lowest = Number.POSITIVE_INFINITY;
  let first = 0;
  let last = 0;
  for (const run of footprint.runs) {
    if (run[0] < lowest) {
      lowest = run[0];
      first = run[1];
      last = run[2];
    } else if (run[0] === lowest) {
      first = Math.min(first, run[1]);
      last = Math.max(last, run[2]);
    }
  }
  const rise = footprint.h - footprint.base + lowest;
  return { column: Math.floor((first + last + 1) / 2 / cellPixels), rowsUp: Math.ceil(rise / cellPixels) - 1 };
}

/**
 * A stamp's cells in a frame's own unmirrored space, its origin on the ground cell.
 *
 * @param stamp The stamp.
 * @param ground The cell the frame stands on.
 * @returns The cells as `[column, rowsUp]`, row by row from the mask's top, each row from the left.
 */
export function zoneStampFrameCells(stamp: ZoneStampDefinition, ground: FrameGroundCell): [number, number][] {
  const cells: [number, number][] = [];
  stamp.runs.forEach(([first, length], row) => {
    for (let across = 0; across < length; across += 1) {
      cells.push([ground.column - stamp.originColumn + first + across, ground.rowsUp + stamp.originRow - row]);
    }
  });
  return cells;
}

/**
 * What a prop's type offers to paint around it.
 *
 * @param prop The prop, or undefined for none.
 * @param types The game's object types.
 * @param zoneTypes The game's zone types.
 * @returns The offer, or null for a prop with no behaviour, a type that declares no stamp, or a stamp whose zone
 *   type the game does not register.
 */
export function zoneStampOffer(prop: PropInstance | undefined, types: readonly ObjectTypeDefinition[], zoneTypes: readonly ZoneTypeDefinition[]): ZoneStampOffer | null {
  const stamp = types.find((type) => type.type === prop?.behaviour?.type)?.zoneStamp;
  const zoneType = stamp === undefined ? undefined : zoneTypes.find((type) => type.id === stamp.zoneType);
  return stamp === undefined || zoneType === undefined ? null : { zoneType, activation: stamp.activation === true };
}

/**
 * Whether two lists of cells hold the same cells in the same order.
 *
 * @param a One list, or undefined for none.
 * @param b The other.
 * @returns True when equal.
 */
function sameCells(a: readonly (readonly [number, number])[] | undefined, b: readonly (readonly [number, number])[]): boolean {
  return a !== undefined && a.length === b.length && a.every((cell, index) => cell[0] === b[index]?.[0] && cell[1] === b[index]?.[1]);
}

/**
 * The topmost zone layer of a map that already holds a zone type. A zone layer is kept canonical by every edit
 * (`editZoneCells`): its table lists exactly the ids its cells hold, so a layer whose last cell of a type was erased
 * no longer lists it and is not chosen.
 *
 * @param map The map.
 * @param zoneType The zone type's id.
 * @returns The layer, or undefined when no zone layer's cells hold the type.
 */
function topmostZoneLayerOf(map: MapDocument, zoneType: string): ZoneLayer | undefined {
  for (let index = map.layers.length - 1; index >= 0; index -= 1) {
    const layer = map.layers[index];
    if (layer?.kind === "zone" && layer.types.includes(zoneType)) {
      return layer;
    }
  }
  return undefined;
}

/**
 * The footprint a prop stands by: its initial state's, or, where that state stamps nothing, its frame's size with no
 * runs.
 *
 * @param prop The prop.
 * @param context The compile context.
 * @param tileSize Tile size in pixels.
 * @returns The footprint, or null when no pack sizes the frame.
 */
function standingFootprint(prop: PropInstance, context: CompileContext, tileSize: number): PropFootprint | null {
  const size = frameSizeOf(context, frameId(prop.frame.tileset, null, prop.frame.tile));
  if (size === undefined) {
    return null;
  }
  return initialFootprint(prop, context, tileSize) ?? { w: size.w, h: size.h, base: size.h, runs: [] };
}

/**
 * The command that paints a prop's zone stamp (ADR-096 decision 38): the mask's cells that lie on the map take the
 * stamp's zone type on the topmost zone layer already holding that type, or on a new layer named for the type on top
 * of the map when none does; with `activation` set, the prop's activation cells become the mask. One command, so one
 * undo takes all of it back.
 *
 * @param map The map.
 * @param layerId The prop's layer.
 * @param propId The prop.
 * @param sources The game's object types and zone types, the compile context, the tile size and what gives a new
 *   zone layer its id, asked only when a layer is added.
 * @returns The command and the zone layer it paints, null when no cell of the mask lies on the map; `command` null
 *   when the zone and the cells are already so; or
 *   why nothing can be painted, as a code.
 * @throws Error when the prop does not exist.
 */
export function zoneStampCommand(map: MapDocument, layerId: string, propId: string, sources: { readonly types: readonly ObjectTypeDefinition[]; readonly zoneTypes: readonly ZoneTypeDefinition[]; readonly context: CompileContext; readonly tileSize: number; readonly newLayerId: () => string }): { readonly command: Command | null; readonly zoneLayerId: string | null } | "no-stamp" | "no-size" | "off-map" {
  const { prop } = requireProp(map, layerId, propId);
  const behaviour = prop.behaviour;
  const definition = sources.types.find((type) => type.type === behaviour?.type);
  const stamp = definition?.zoneStamp;
  const zoneType = stamp === undefined ? undefined : sources.zoneTypes.find((type) => type.id === stamp.zoneType);
  if (behaviour === undefined || definition === undefined || stamp === undefined || zoneType === undefined) {
    return "no-stamp";
  }
  const footprint = standingFootprint(prop, sources.context, sources.tileSize);
  if (footprint === null) {
    return "no-size";
  }
  const cells = zoneStampFrameCells(stamp, footprintGroundCell(footprint, sources.tileSize / COLLISION_CELLS_PER_TILE));
  const stamped: PropBehaviour = { ...behaviour, activation: cells };
  const look = activationLook({ ...prop, behaviour: stamped }, null, sources.context, () => undefined, sources.tileSize);
  if (look === null) {
    return "off-map";
  }
  const gridWidth = map.width * COLLISION_CELLS_PER_TILE;
  const gridHeight = map.height * COLLISION_CELLS_PER_TILE;
  const indices: number[] = [];
  for (let y = 0; y < look.cells.height; y += 1) {
    for (let x = 0; x < look.cells.width; x += 1) {
      const column = look.cells.x0 + x;
      const row = look.cells.y0 + y;
      if (look.cells.cells[y * look.cells.width + x] === 1 && column >= 0 && row >= 0 && column < gridWidth && row < gridHeight) {
        indices.push(row * gridWidth + column);
      }
    }
  }
  const existing = topmostZoneLayerOf(map, stamp.zoneType);
  const added = existing === undefined && indices.length > 0 ? newLayer(map, "zone", sources.newLayerId(), zoneType.label, undefined) : undefined;
  const target = existing ?? added;
  const paint = target === undefined ? null : paintZoneCommand(added === undefined ? map : { ...map, layers: [...map.layers, added] }, target.id, indices, stamp.zoneType);
  const commands: Command[] = [];
  if (paint !== null) {
    if (added !== undefined) {
      commands.push(addLayerCommand(map, added));
    }
    commands.push(paint);
  }
  if (stamp.activation === true && !sameCells(behaviour.activation, cells)) {
    commands.push(setPropBehaviourCommand(map, layerId, propId, stamped));
  }
  return { command: commands.length === 0 ? null : commands.length === 1 ? (commands[0] as Command) : { kind: "compound", commands }, zoneLayerId: target?.id ?? null };
}
